import { useState } from 'react'
import { AdminPanelButton } from './AdminPanelButton'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Link2, Copy, Check } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'

export function GenerateLoginLinkSection() {
  const [email, setEmail] = useState('')
  const [expiryMinutes, setExpiryMinutes] = useState(60)
  const [loginUrl, setLoginUrl] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoginUrl('')
    setLoading(true)
    try {
      const baseUrl = window.location.origin
      const res = await fetch(`${API_BASE_URL}auth/generate-login-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim(),
          expiry_minutes: expiryMinutes,
          base_url: baseUrl,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.detail || data.error || 'Failed to generate link')
        return
      }
      const url = data.login_url || (baseUrl + `/#/login?token=${data.token}`)
      setLoginUrl(url)
    } catch {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!loginUrl) return
    try {
      await navigator.clipboard.writeText(loginUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          Generate login link
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Create a one-time sign-in link for a user. They can open the link to sign in without entering a password.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleGenerate} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gen-email">User email</Label>
            <Input
              id="gen-email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-expiry">Link valid for (minutes)</Label>
            <Input
              id="gen-expiry"
              type="number"
              min={5}
              max={60}
              value={expiryMinutes}
              onChange={(e) => setExpiryMinutes(Number(e.target.value) || 60)}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <AdminPanelButton type="submit" disabled={loading}>
            {loading ? 'Generating…' : 'Generate link'}
          </AdminPanelButton>
        </form>
        {loginUrl && (
          <div className="space-y-2 pt-4 border-t">
            <Label>Login link (one-time use)</Label>
            <div className="flex gap-2">
              <Input readOnly value={loginUrl} className="font-mono text-sm" />
              <AdminPanelButton type="button" size="icon" onClick={handleCopy} title="Copy">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </AdminPanelButton>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
